import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { ArrowLeft, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useClipPolling } from '@/hooks/use-polling';
import { useClipStudioRenderData } from '@/hooks/use-clip-studio-render-data';
import { UPDATE_CLIP_DATA } from '@/queries/queries';
import {
  FREECUT_AI_RUNTIME_URL,
  freecutLanguage,
  loadFreecutEditor,
  type FreecutEditorHandle,
} from '@/lib/freecut-studio/freecutEditor';
import { createStudioSaver, type StudioSaveState } from '@/lib/freecut-studio/studioSaver';
import { applyStudioShadowTheme, STUDIO_THEME_STYLE } from '@/lib/freecut-studio/studioTheme';
import '@/lib/freecut-studio/studioTheme.css';
import {
  savedStudioProject,
  studioImport,
  studioSavePatch,
  type FreecutProject,
} from '@/lib/freecut-studio/studioStart';

type Stage =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'unavailable' }
  | { kind: 'source-preparing' }
  | { kind: 'source-unavailable' }
  | { kind: 'unsupported'; composition: string }
  | { kind: 'failed'; message: string };

const download = (blob: Blob, name: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * FreeCut Studio: the clip, as it would export now (exported before or not), opened in FreeCut,
 * a full multi-track editor embedded from public/freecut/. Edits are saved to
 * `clip.data.freecutProject`; exports render in the browser and download.
 */
const ClipStudio = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: clip, loading, error } = useClipPolling(id, { enabled: !!id });
  const [updateClipData] = useMutation(UPDATE_CLIP_DATA);
  const container = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [saveState, setSaveState] = useState<StudioSaveState | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  // Each "start over" re-imports the clip once.
  const [restarts, setRestarts] = useState(0);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const clipLoaded = !!clip;
  const renderData = useClipStudioRenderData(clip);
  // The saved studio project resumes; otherwise (and on "start over") the studio imports the
  // clip's render data, which needs the clip's project/library and templates loaded first.
  const resumes = restarts === 0 && !!clip && !!savedStudioProject(clip);
  const waitingForRenderData = !resumes && !renderData.ready;

  const saver = useMemo(
    () =>
      createStudioSaver<FreecutProject>(async (project) => {
        await updateClipData({ variables: { id, data: studioSavePatch(project) } });
      }, setSaveState),
    [id, updateClipData],
  );

  useEffect(() => {
    if (!clipLoaded || !clip || !container.current || waitingForRenderData) return;
    let cancelled = false;
    let handle: FreecutEditorHandle | null = null;
    const element = container.current;
    setStage({ kind: 'loading' });
    void (async () => {
      try {
        const editor = await loadFreecutEditor();
        let project: FreecutProject;
        const resumed = restarts === 0 ? savedStudioProject(clip) : null;
        if (resumed) {
          project = resumed;
        } else {
          if (renderData.sourceStatus !== 'ready') {
            if (!cancelled) {
              setStage({ kind: renderData.sourceStatus === 'preparing' ? 'source-preparing' : 'source-unavailable' });
            }
            return;
          }
          const start = studioImport(await renderData.load(), editor.SUPPORTED_AUTOCLIPPER_COMPOSITIONS);
          if (start.kind !== 'import') {
            if (!cancelled) setStage(start);
            return;
          }
          const imported = await editor.importAutoclipperRender(start.request);
          project = imported.project;
          if (!cancelled) setWarnings(imported.warnings);
          saver.save(project);
        }
        if (cancelled) return;
        handle = editor.mountFreecutEditor(element, {
          project,
          singleProject: true,
          language: freecutLanguage(i18n.language),
          aiRuntimeUrl: FREECUT_AI_RUNTIME_URL,
          onSave: (saved) => saver.save(saved),
          onExport: download,
          onClose: () => navigate(-1),
        });
        applyStudioShadowTheme(element);
        await handle.ready;
        if (!cancelled) setStage({ kind: 'ready' });
      } catch (err) {
        if (!cancelled) setStage({ kind: 'failed', message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
      void handle?.unmount();
      void saver.flush();
    };
    // The studio mounts once per clip (and again when starting over); later polls of the
    // clip must not remount the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipLoaded, restarts, waitingForRenderData]);

  const message = (() => {
    if (error) return t('clipEdit.errorLoading');
    if (!loading && !clip) return t('clipEdit.clipNotFound');
    switch (stage.kind) {
      case 'loading':
        return t('clipStudio.loading');
      case 'unavailable':
        return t('clipStudio.unavailable');
      case 'source-preparing':
        return t('clipStudio.sourcePreparing');
      case 'source-unavailable':
        return t('clipStudio.sourceUnavailable');
      case 'unsupported':
        return t('clipStudio.unsupported', { composition: stage.composition });
      case 'failed':
        return `${t('clipStudio.loadFailed')} ${stage.message}`;
      default:
        return null;
    }
  })();

  return (
    <div className="studio-shell flex h-screen flex-col" style={STUDIO_THEME_STYLE}>
      <header className="flex items-center gap-3 border-b px-4 py-2">
        <Button className="studio-ghost" variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('clipStudio.back')}
        </Button>
        <h1 className="text-sm font-semibold">{t('clipStudio.title')}</h1>
        <span className="studio-badge rounded px-1.5 py-0.5 text-[10px] font-medium uppercase">
          {t('clipStudio.beta')}
        </span>
        <span className="studio-muted truncate text-xs">{clip?.name}</span>
        <div className="ml-auto flex items-center gap-3">
          {saveState && (
            <span
              role="status"
              className={saveState === 'failed' ? 'text-xs text-destructive' : 'studio-muted text-xs'}
            >
              {t(`clipStudio.${saveState === 'failed' ? 'saveFailed' : saveState}`)}
            </span>
          )}
          <Button
            className="studio-outline"
            variant="outline"
            size="sm"
            disabled={stage.kind !== 'ready'}
            onClick={() => setConfirmRestart(true)}
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            {t('clipStudio.restart')}
          </Button>
        </div>
      </header>
      {warnings.length > 0 && stage.kind === 'ready' && (
        <p className="studio-warning studio-muted border-b px-4 py-1 text-xs">
          {t('clipStudio.warnings', { list: warnings.join('; ') })}
        </p>
      )}
      <div className="relative min-h-0 flex-1">
        <div ref={container} data-testid="freecut-studio" className="absolute inset-0" style={STUDIO_THEME_STYLE} />
        {message && (
          <div className="studio-shell absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
            {stage.kind === 'loading' && !error && (
              <div className="studio-spinner h-5 w-5 animate-spin rounded-full border-2" />
            )}
            <p className="studio-muted max-w-md text-sm">{message}</p>
            {stage.kind !== 'loading' && stage.kind !== 'failed' && (
              <Button className="studio-outline" variant="outline" size="sm" onClick={() => navigate('/clips')}>
                {t('clipStudio.goToClips')}
              </Button>
            )}
          </div>
        )}
      </div>
      <Dialog open={confirmRestart} onOpenChange={setConfirmRestart}>
        <DialogContent className="studio-shell" style={STUDIO_THEME_STYLE}>
          <DialogHeader>
            <DialogTitle>{t('clipStudio.restartTitle')}</DialogTitle>
            <DialogDescription className="studio-muted">{t('clipStudio.restartDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="studio-outline" variant="outline" onClick={() => setConfirmRestart(false)}>
              {t('clipStudio.cancel')}
            </Button>
            <Button
              className="studio-primary"
              onClick={() => {
                setConfirmRestart(false);
                setRestarts((n) => n + 1);
              }}
            >
              {t('clipStudio.confirmRestart')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClipStudio;
